import React, { useState, useMemo, useCallback } from 'react';
import { Row, Col } from 'antd';
import {
  useDrag,
  useDrop,
  DragSourceMonitor,
  DragElementWrapper,
  DragSourceOptions,
} from 'react-dnd';
import styled from '@emotion/styled';
import produce from 'immer';
import { v4 as uuidv4 } from 'uuid';
// import { camelCase } from 'lodash';

import { IElementSchema } from '../../types/form';
export interface IOption {
  label: string;
  value: string;
}

const getOptionLabel = (option: IOption) => option.label;
const getOptionValue = (option: IOption) => option.value;
const getElementTitle = (element: IElementSchema) => element.title;
const getElementName = (element: IElementSchema) => element.name;

/**
 * Styled Components
 */

type StyledDropZoneProps = {
  isOver: boolean;
  canDrop: boolean;
};
type StyledDraggableOptionProps = {
  isDragging: boolean;
};

const lineHeight = 54;

const StyledDropZoneBasket = styled('div')<StyledDropZoneProps>`
  margin: 20px 0;
  background-color: gray;
  min-height: 75px;
  min-width: 100%;
  background-color: ${props => (props.isOver && props.canDrop ? '#eff2f6' : 'initial')};
`;
const StyledDropZone = styled('div')<StyledDropZoneProps>`
  border: 1px dashed #dfe4ea;
  min-height: ${lineHeight}px;
  border-radius: 4px;
  background-color: ${props => (props.isOver && props.canDrop ? '#eff2f6' : 'initial')};
  & > div {
    display: block;
  }
`;
const StyledDraggableOption = styled('div')<StyledDraggableOptionProps>`
  background: #eff2f6;
  border: 1px solid #dfe4ea;
  align-items: center;
  padding: 5px 10px;
  border-radius: 4px;
  cursor: grab;
  opacity: ${props => (props.isDragging ? 0.5 : 'initial')};
  display: inline-block;
`;

const StyledLabelCol = styled(Col)`
  max-width: 100px;
  line-height: ${lineHeight}px;
`;

const StyledInputCol = styled(Col)`
  max-width: 500px;
`;

type DragNDropOptionsProps = {
  name: string;
  value: Record<string, any>;
  elements: IElementSchema[];
  options: IOption[];
  onChange: (event: DragNDropOptionsChangeEvent) => void;
  errors?: any;
};

type DragNDropOptionsValue = { [key: string]: string | undefined };
type DragNDropOptionsChangeEvent = { name: string; value: DragNDropOptionsValue };

const DROPZONE_DEFAULT_ID = 'default_basket';

const DragNDropOptions = (props: DragNDropOptionsProps) => {
  // console.log(props);
  const { name, elements, options, value = {}, onChange, errors } = props;
  const [type] = useState(uuidv4());

  const onDrop = useCallback(
    (dropZoneId, optionId) => {
      const newValue = produce(value, draftValue => {
        const keyWithSameValue = Object.keys(value).find(key => value[key] === optionId);

        // case: item drag&drop on the default drop zone
        if (dropZoneId === DROPZONE_DEFAULT_ID && !keyWithSameValue) {
          return;
        }

        // case: move item to default drop zone from other drop zone
        if (dropZoneId === DROPZONE_DEFAULT_ID && keyWithSameValue) {
          draftValue[keyWithSameValue] = undefined;
          return;
        }

        // case: move item between drop zones (no swap)
        if (!value[dropZoneId] && keyWithSameValue) {
          draftValue[keyWithSameValue] = undefined;
        }

        // case: swap items between drop zones
        if (!!value[dropZoneId] && keyWithSameValue) {
          draftValue[keyWithSameValue] = value[dropZoneId];
        }

        // set item to drop zone
        draftValue[dropZoneId] = optionId;
      });

      // setValue(newValue);
      onChange({ name, value: newValue });
    },
    [value, name, onChange]
  );

  const getDropZoneItem = useCallback(
    (element: IElementSchema) => {
      const option = options.find(option => option.value === value[element.name]);
      return (
        option && (
          <DraggableItem type={type} id={option.value} onDrop={onDrop}>
            {({ drag, isDragging }) => (
              <StyledDraggableOption ref={drag} isDragging={isDragging}>
                <div>{option.label}</div>
              </StyledDraggableOption>
            )}
          </DraggableItem>
        )
      );
    },
    [value, onDrop, type, options]
  );

  const notSelectedOptions = useMemo(() => {
    return options.filter(option => !Object.values(value).includes(option.value));
  }, [value, options]);

  return (
    <>
      {elements.map((element: IElementSchema, index: number) => {
        const elementTitle = getElementTitle(element);
        const elementName = getElementName(element);
        const elementError = errors && errors[name] && errors[name][elementName];

        return (
          <Row key={index} style={{ marginBottom: 10 }}>
            <StyledLabelCol xs={24} md={5}>
              {elementTitle}
            </StyledLabelCol>
            <StyledInputCol xs={24} md={18}>
              <DropZone type={type} id={elementName}>
                {({ drop, isOver, canDrop }) => (
                  <StyledDropZone ref={drop} isOver={isOver} canDrop={canDrop}>
                    <ValidationWrapper validationMessage={elementError && elementError.message}>
                      {getDropZoneItem(element)}
                    </ValidationWrapper>
                  </StyledDropZone>
                )}
              </DropZone>
            </StyledInputCol>
          </Row>
        );
      })}
      <Row gutter={[16, 16]} align="middle">
        <DropZone type={type} id={DROPZONE_DEFAULT_ID}>
          {({ drop, isOver, canDrop }) => (
            <StyledDropZoneBasket ref={drop} isOver={isOver} canDrop={canDrop}>
              {notSelectedOptions.map((option: IOption, index: number) => {
                const optionLabel = getOptionLabel(option);
                const optionValue = getOptionValue(option);
                return (
                  <Col key={index}>
                    <DraggableItem type={type} id={optionValue} onDrop={onDrop}>
                      {({ drag, isDragging }) => (
                        <StyledDraggableOption ref={drag} isDragging={isDragging}>
                          <div>{optionLabel}</div>
                        </StyledDraggableOption>
                      )}
                    </DraggableItem>
                  </Col>
                );
              })}
            </StyledDropZoneBasket>
          )}
        </DropZone>
      </Row>
    </>
  );
};

const MemoizedDragNDropOptions = React.memo(DragNDropOptions);
export default MemoizedDragNDropOptions;

/**
 * Drag & Drop Components
 */

type DraggableItemChildrenAttrs = {
  drag: DragElementWrapper<DragSourceOptions>;
  isDragging: boolean;
};

interface DraggableItem {
  id: string;
}

const DraggableItem = React.memo(
  ({
    type,
    id,
    onDrop,
    children,
  }: {
    type: string;
    id: string;
    children: (attrs: DraggableItemChildrenAttrs) => React.ReactNode;
    onDrop: (dropZoneId: string, optionId: string) => void;
  }) => {
    const [{ isDragging }, drag] = useDrag({
      item: { id } as DraggableItem,
      type,
      collect: monitor => ({
        isDragging: monitor.isDragging(),
      }),
      end: (item: any, monitor: DragSourceMonitor) => {
        const dropResult = monitor.getDropResult() as DraggableItem;
        if (dropResult) {
          onDrop(dropResult.id, item.id);
        }
      },
    });

    return <>{children({ drag, isDragging })}</>;
  }
);

type DropZoneChildrenAttrs = {
  drop: DragElementWrapper<any>;
  isOver: boolean;
  canDrop: boolean;
};

const DropZone = React.memo(
  ({
    type,
    id,
    children,
  }: {
    type: string;
    id: string;
    children: (attrs: DropZoneChildrenAttrs) => React.ReactNode;
  }) => {
    const [{ isOver, canDrop }, drop] = useDrop({
      accept: type,
      drop: () => ({ id }),
      collect: monitor => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    });
    return <>{children({ drop, isOver, canDrop })}</>;
  }
);

const ValidationMessage = styled.div`
  color: red;
  margin-left: 10px;
  line-height: 50px;
`;

const ValidationWrapper = React.memo(
  ({ validationMessage, children }: { validationMessage: string; children?: React.ReactNode }) => {
    return (
      <>
        {validationMessage && <ValidationMessage>{validationMessage}</ValidationMessage>}
        {!validationMessage && children}
      </>
    );
  }
);
ValidationWrapper.displayName = 'ValidationWrapper';
